import React from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

/** Modal when session recording upload fails — retry or continue without video. */
export function RecordingUploadFailedModal({
  open,
  errorMessage,
  onRetry,
  onContinue,
  onCancel,
  continueLabel = 'Continue without recording',
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      data-testid="recording-upload-failed-modal"
    >
      <div className="w-full max-w-md rounded-xl border bg-card p-6 shadow-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">Recording upload failed</h2>
            <p className="text-sm text-muted-foreground">
              We could not upload your session recording. You can retry the upload or continue —
              your assessment answers will still be submitted.
            </p>
            {errorMessage && (
              <p className="text-xs text-muted-foreground">{errorMessage}</p>
            )}
          </div>
        </div>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
          {onCancel && (
            <Button variant="outline" onClick={onCancel} data-testid="recording-upload-cancel">
              Cancel
            </Button>
          )}
          <Button variant="outline" onClick={onRetry} data-testid="recording-upload-retry">
            Retry upload
          </Button>
          <Button onClick={onContinue} data-testid="recording-upload-continue">
            {continueLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Inline upload progress during submit/abandon. */
export function RecordingUploadProgress({ progress, uploading }) {
  if (!uploading) return null;
  const pct = progress != null ? Math.min(100, Math.max(0, progress)) : null;
  return (
    <p className="text-xs text-muted-foreground" data-testid="recording-upload-progress">
      Uploading recording
      {pct != null ? ` (${pct}%)` : '…'}
    </p>
  );
}
